import queryString from 'query-string';
import { ENDPOINTS } from 'config/endpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { useConfirmModal } from 'context/confirmModal';
import { MutationMeta, withMeta } from 'crono-fe-common/types/mutationMeta';
import { invalidateTimelineQueries } from '../timeline/useSearchTimeline';

export default function useDeleteTask() {
  const queryClient = useQueryClient();

  const deleteTask = ({ taskId }: { taskId: number }) => {
    const queryParams = queryString.stringify({ taskId });

    const request: Request = {
      url: `${ENDPOINTS.task.main}?${queryParams}`,
      config: {
        method: 'delete',
      },
    };
    return client(request);
  };

  const { data, mutate, ...rest } = useMutation<
    Response<null>,
    CronoError,
    { taskId: number }
  >(deleteTask, {
    onSettled: () => {
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.next]);
      queryClient.invalidateQueries([ENDPOINTS.task.total]);
      queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]);
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.overdue]);
      queryClient.invalidateQueries([ENDPOINTS.task.user.analytics]);
      queryClient.invalidateQueries([ENDPOINTS.task.automationErrors]);

      queryClient.invalidateQueries([ENDPOINTS.task.aggregate]);
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.strategy.prospect),
      });
      queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]);

      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.details]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.main]);
      invalidateTimelineQueries(queryClient);
    },
  });

  return { data, mutate, ...rest };
}

export function useDeleteTaskWithConfirm(
  onSuccess?: () => void,
  meta?: MutationMeta,
) {
  const { mutate: deleteTask, ...rest } = useDeleteTask();
  const { openModal: openConfirmModal } = useConfirmModal();

  function deleteTaskWithConfirm(id: number) {
    openConfirmModal({
      title: 'Are you sure you want to delete this task?',
      text: 'The task will be permanently deleted',
      cancelText: 'No',
      confirmText: 'Yes',
      confirmFunction: () => {
        deleteTask(withMeta({ taskId: id }, meta ?? {}), { onSuccess });
      },
      cancelFunction: () => null,
    });
  }

  return {
    ...rest,
    deleteTaskWithConfirm,
  };
}
